import { ChevronDownIcon, InfoIcon, MapPinIcon, XIcon } from 'lucide-react'
import {
	createContext,
	type FC,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react'
import { cn } from '#app/utils/misc.tsx'
import { REGIONS } from '#app/utils/search-lib'

/**
 * ETT regionval för hela sidan (variant A "i chattrutan", vald av Johannes 2026-09-10).
 *
 * Regionen bor i URL-parametern ?region= och ägs av routen
 * (app/routes/_app._index.tsx, handleRegionSelect). ChatPage lägger värdet +
 * bytesfunktionen i den här contexten så att tre ställen kan läsa SAMMA val
 * utan prop-drilling genom assistant-ui-trädet:
 *
 *   - förstasidan (tomläget): meningen "Söker i <region ▾>" under fältet
 *     (RegionUnderRow) - samma utseende som im.se:s startsida
 *   - resultatvyn: ett litet pill nere till vänster i composern (RegionChip)
 *   - Snabbsök-kortet: läser bara regionen (kortets egen väljare är borttagen)
 *
 * Alla varianter använder en osynlig native <select> ovanpå den synliga ytan:
 * plattformens egen väljare klarar 21 regioner lika bra som dagens 5.
 */

export const DEFAULT_REGION = 'all'

export interface RegionContextValue {
	region: string
	/** Saknas = kontrollerna renderas inte (t.ex. embed). */
	onSelectRegion?: (region: string) => void
}

export const RegionContext = createContext<RegionContextValue>({
	region: DEFAULT_REGION,
})

export function useRegion(): RegionContextValue {
	return useContext(RegionContext)
}

export const REGION_INFO_TEXT =
	'Vald region prioriteras i AI-svaret och i sökrutan. Du ser fortfarande andra källor.'

export function regionLabel(region: string): string {
	return REGIONS.find(r => r.value === region)?.label ?? REGIONS[0]?.label ?? ''
}

/** Pillets etikett på desktop - "Region" är underförstått av kartnålen. */
export function chipLabel(region: string): string {
	if (region === DEFAULT_REGION) return 'Alla regioner'
	return regionLabel(region).replace(/^Region /, '')
}

/** Pillets etikett på mobil - får inte krocka med skicka-knappen. */
const SHORT_LABELS: Record<string, string> = {
	all: 'Alla',
	stockholm: 'Stockholm',
	vgr: 'VGR',
	skane: 'Skåne',
	uppsala: 'Uppsala',
}
export function shortLabel(region: string): string {
	return SHORT_LABELS[region] ?? chipLabel(region)
}

// Statiska id:n (inte useId): kontrollen finns EN gång per vy, och useId gav
// olika id på server och klient i referensimplementationen på im.se.
const UNDER_SELECT_ID = 'assistant-region-under'
const CHIP_SELECT_ID = 'assistant-region-chip'
const INFO_ID = 'assistant-region-info'

/** Osynlig native select ovanpå en synlig yta. */
const RegionSelect: FC<{
	id: string
	region: string
	onChange: (next: string) => void
}> = ({ id, region, onChange }) => (
	<select
		id={id}
		value={region}
		onChange={e => onChange(e.target.value)}
		aria-label="Välj region"
		title={`Region: ${regionLabel(region)}`}
		className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
	>
		{REGIONS.map(r => (
			<option key={r.value} value={r.value}>
				{r.label}
			</option>
		))}
	</select>
)

/**
 * Meningen under fältet på förstasidan: "Söker i <Alla regioner ▾> (i)".
 * Regionnamnet är det klickbara (dotted underline), grönt när en region är vald.
 * Samma rad står under navbarens sökfält; där med egna id:n, eftersom dialogen
 * kan vara öppen ovanpå förstasidan.
 */
export const RegionUnderRow: FC<{
	selectId?: string
	infoId?: string
	testId?: string
}> = ({
	selectId = UNDER_SELECT_ID,
	infoId = INFO_ID,
	testId = 'region-under-row',
}) => {
	const { region, onSelectRegion } = useRegion()
	if (!onSelectRegion) return null
	const active = region !== DEFAULT_REGION
	return (
		<p
			data-testid={testId}
			className="relative mt-2 flex flex-wrap items-center justify-start gap-x-1.5 gap-y-1 px-1 text-left text-xs text-muted-foreground"
		>
			<span>Söker i</span>
			<span
				className={cn(
					'relative inline-flex items-center gap-1 font-semibold underline decoration-dotted underline-offset-2 focus-within:rounded focus-within:ring-2 focus-within:ring-green-600/50',
					active ? 'text-green-600 dark:text-green-300' : 'text-foreground',
				)}
			>
				<span aria-hidden="true" className="inline-flex items-center gap-1">
					{regionLabel(region)}
					<ChevronDownIcon className="h-3 w-3 shrink-0" />
				</span>
				<RegionSelect id={selectId} region={region} onChange={onSelectRegion} />
			</span>
			<RegionInfo side="bottom" id={infoId} />
		</p>
	)
}

/**
 * Pillet nere till vänster i composern (resultatvyn): kartnål + regionnamn + ▾.
 * På mobil krymper texten till kortnamnet ("VGR", "Skåne", "Alla").
 */
export const RegionChip: FC = () => {
	const { region, onSelectRegion } = useRegion()
	if (!onSelectRegion) return null
	const active = region !== DEFAULT_REGION
	return (
		<span className="inline-flex items-center gap-1">
			<span
				data-testid="region-chip"
				className={cn(
					'relative inline-flex shrink-0 items-center rounded-full border transition-colors focus-within:ring-2 focus-within:ring-green-600/50',
					active
						? 'border-green-600/40 bg-green-600/10 text-green-600 dark:border-green-700 dark:bg-green-900/30 dark:text-green-200'
						: 'border-border bg-background text-muted-foreground hover:border-foreground/40 hover:text-foreground',
				)}
			>
				<span
					aria-hidden="true"
					className="inline-flex items-center gap-1 py-1 pl-2 pr-1.5 text-xs font-medium"
				>
					<MapPinIcon className="h-3.5 w-3.5 shrink-0" />
					<span className="sm:hidden">{shortLabel(region)}</span>
					<span className="hidden max-w-[9rem] truncate sm:inline">
						{chipLabel(region)}
					</span>
					<ChevronDownIcon className="h-3 w-3 shrink-0" />
				</span>
				<RegionSelect
					id={CHIP_SELECT_ID}
					region={region}
					onChange={onSelectRegion}
				/>
			</span>
			<RegionInfo side="top" />
		</span>
	)
}

/**
 * (i)-rutan - bär hela förklaringen av vad regionvalet gör. Öppnas på hover,
 * tangentbordsfokus och klick (klick behövs på pekskärm). Hover och klick
 * spåras separat så musens mouseenter + klick inte tar ut varandra.
 */
export const RegionInfo: FC<{ side: 'top' | 'bottom'; id?: string }> = ({
	side,
	id: infoId = INFO_ID,
}) => {
	const [hovered, setHovered] = useState(false)
	const [pinned, setPinned] = useState(false)
	const open = hovered || pinned
	return (
		<span className="relative inline-flex">
			<button
				type="button"
				aria-label="Om regionval"
				aria-expanded={open}
				aria-describedby={open ? infoId : undefined}
				onClick={() => setPinned(p => !p)}
				onMouseEnter={() => setHovered(true)}
				onMouseLeave={() => setHovered(false)}
				onFocus={() => setHovered(true)}
				onBlur={() => {
					setHovered(false)
					setPinned(false)
				}}
				className="rounded-full text-muted-foreground/70 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600/50"
			>
				<InfoIcon className="h-3.5 w-3.5" aria-hidden="true" />
			</button>
			{open && (
				<span
					id={infoId}
					role="tooltip"
					className={cn(
						'absolute left-0 z-30 w-[min(18rem,calc(100vw-2.5rem))] rounded-lg border border-border bg-popover px-3 py-2 text-xs font-normal leading-snug text-popover-foreground shadow-lg',
						side === 'top' ? 'bottom-full mb-2' : 'top-full mt-2',
					)}
				>
					{REGION_INFO_TEXT}
				</span>
			)}
		</span>
	)
}

/**
 * Engångstipset "Välj din region för bättre träffar" — pekar på pillen i
 * chattrutan (resultatvyn). Visas EN gång per webbläsare (localStorage), tonar
 * ut efter 6 s (hover/tap pausar), och räknas som sett vid ✕ eller vid första
 * regionbytet. Ersätter Snabbsök-kortets gamla tips som pekade på kortets
 * numera borttagna väljare (Johannes 2026-09-10: "ska bara synas en gång per
 * användare så de slipper se varje gång").
 */
export const REGION_HINT_KEY = 'assistant-region-hint-seen'
export const RegionHint: FC = () => {
	const { region, onSelectRegion } = useRegion()
	const [show, setShow] = useState<boolean>(() => {
		if (typeof window === 'undefined') return false
		try {
			return window.localStorage.getItem(REGION_HINT_KEY) == null
		} catch {
			return false
		}
	})
	const [fading, setFading] = useState(false)
	const hoveredRef = useRef(false)
	const initialRegion = useRef(region)
	const dismiss = useCallback(() => {
		setShow(false)
		try {
			window.localStorage.setItem(REGION_HINT_KEY, '1')
		} catch {
			/* ignore */
		}
	}, [])
	// Första regionbytet räknas som "tipset läst".
	useEffect(() => {
		if (region !== initialRegion.current) dismiss()
	}, [region, dismiss])
	// Auto-fade efter 6 s (500 ms transition). Pekare över tipset PAUSAR: när
	// timern går ut medan hoveredRef är satt läggs den om (1 s), så fade:n
	// återupptas när pekaren lämnar. (Första versionen bara returnerade ur
	// callbacken — då blev tipset stående för evigt om man råkade hovra vid 6 s.)
	useEffect(() => {
		if (!show) return
		let timer: ReturnType<typeof setTimeout> | undefined
		const done = () => {
			if (hoveredRef.current) {
				timer = setTimeout(done, 1000)
				return
			}
			dismiss()
		}
		const fade = () => {
			if (hoveredRef.current) {
				timer = setTimeout(fade, 1000)
				return
			}
			setFading(true)
			timer = setTimeout(done, 500)
		}
		timer = setTimeout(fade, 6000)
		return () => {
			if (timer) clearTimeout(timer)
		}
	}, [show, dismiss])
	if (!onSelectRegion || !show) return null
	return (
		<div
			role="status"
			data-testid="region-hint"
			onMouseEnter={() => {
				hoveredRef.current = true
			}}
			onMouseLeave={() => {
				hoveredRef.current = false
			}}
			// Självrensande touch-lås (samma kontrakt som kortets gamla tips): ett
			// tap pausar fade-timern ~800 ms men får aldrig pinna den för evigt.
			onTouchEnd={() => {
				hoveredRef.current = true
				setTimeout(() => {
					hoveredRef.current = false
				}, 800)
			}}
			className={cn(
				// Inline till HÖGER om pillen, i samma rad som skicka-knappen: täcker
				// varken platshållaren eller svaret, och pilen (before:) pekar på pillen.
				'relative ml-2 inline-flex min-w-0 items-center gap-1.5 rounded-md border border-green-600/40 bg-green-50 px-2 py-1 text-[11px] leading-snug text-green-800 shadow-sm transition-opacity duration-500 dark:border-green-600/50 dark:bg-green-950 dark:text-green-300',
				"before:absolute before:-left-[5px] before:top-1/2 before:h-2.5 before:w-2.5 before:-translate-y-1/2 before:rotate-45 before:border-b before:border-l before:border-green-600/40 before:bg-green-50 before:content-[''] dark:before:border-green-600/50 dark:before:bg-green-950",
				fading ? 'opacity-0' : 'opacity-100',
			)}
		>
			<span className="truncate">
				<span className="sm:hidden">Välj region för bättre träffar</span>
				<span className="hidden sm:inline">
					Välj din region för bättre träffar
				</span>
			</span>
			<button
				type="button"
				onClick={dismiss}
				aria-label="Dölj regiontips"
				className="-mr-1 shrink-0 rounded p-0.5 text-green-700/60 hover:text-green-700 dark:text-green-400/60 dark:hover:text-green-400"
			>
				<XIcon className="h-3 w-3" />
			</button>
		</div>
	)
}
